import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context';
import { homeRoute } from '../utils/rbac';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import loginImage from '../assets/login-image.jpg';

const DEMO_ROLES = [
  { label: 'Admin', username: 'admin', password: 'admin123' },
  { label: 'CEO', username: 'ceo', password: 'ceo12345' },
  { label: 'Sales Manager', username: 'sales_mgr', password: 'sales123' },
  { label: 'Accountant', username: 'accountant', password: 'acc12345' },
  { label: 'HR Manager', username: 'hr_mgr', password: 'hrm12345' },
  { label: 'Viewer', username: 'viewer', password: 'view1234' },
];

const QC_SHIFTS = [
  { shift: 'A' as const, label: 'Shift A', username: 'qc_a', password: 'qca12345' },
  { shift: 'B' as const, label: 'Shift B', username: 'qc_b', password: 'qcb12345' },
  { shift: 'C' as const, label: 'Shift C', username: 'qc_c', password: 'qcc12345' },
];

export function Login() {
  const navigate = useNavigate();
  const { login } = useApp();
  const [step, setStep] = useState<'staff' | 'shifts' | 'qc'>('staff');
  const [qcShift, setQcShift] = useState<'A' | 'B' | 'C' | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const openQcShift = (shift: 'A' | 'B' | 'C') => {
    setQcShift(shift);
    setUsername('');
    setPassword('');
    setError('');
    setStep('qc');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Enter your user ID and password.');
      return;
    }
    if (step === 'qc' && !qcShift) {
      setError('Choose Shift A, Shift B, or Shift C.');
      return;
    }
    setLoading(true);
    setError('');
    const message = await login(username.trim(), password, step === 'qc' ? qcShift || undefined : undefined);
    if (message) setError(message);
    else {
      let next = '/';
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const parsed = JSON.parse(stored.roles || '[]');
        next = homeRoute(Array.isArray(parsed) ? parsed : [stored.roles]);
      } catch { /* the dashboard remains the default page */ }
      navigate(next);
    }
    setLoading(false);
  };

  const fillDemo = (role: typeof DEMO_ROLES[number]) => {
    setUsername(role.username);
    setPassword(role.password);
    setError('');
  };

  return (
    <div className="min-h-screen flex bg-surface-50 dark:bg-surface-900">
      {/* Left - branding panel (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden items-center justify-center p-12">
        <img
          src={loginImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover scale-105 blur-[3px]"
        />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 max-w-md text-white">
          <p className="text-[11px] font-semibold tracking-[0.32em] uppercase text-accent-200 mb-3 [text-shadow:0_1px_2px_rgba(0,0,0,0.55)]">Oximax agritech Pvt Ltd</p>
          <h2 className="mb-4 leading-none">
            <span className="block text-6xl font-black tracking-tight bg-gradient-to-r from-white via-[#d9f5c4] to-accent-300 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]">DMS</span>
            <span className="mt-3 flex items-center gap-3">
              <span className="h-[2px] w-10 rounded-full bg-accent-300" />
              <span className="text-sm font-medium tracking-[0.18em] uppercase text-accent-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.55)]">Dairy Management System</span>
            </span>
          </h2>
          <p className="text-white/90 text-lg mb-8 leading-relaxed [text-shadow:0_1px_2px_rgba(0,0,0,0.55)]">
            Full dairy plant platform — milk collection at the gate, fat/SNF rate charts, supplier payments, processing, packing, and GST-ready ERP.
          </p>
          <div className="space-y-3">
            {['Gate Pass daily milk entry', 'Fat/SNF rate charts & supplier payouts', 'Processing & packing', 'Finance, inventory & GST'].map(feature => (
              <div key={feature} className="flex items-center gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-accent-400 flex-shrink-0" />
                <span className="text-white/90 [text-shadow:0_1px_2px_rgba(0,0,0,0.55)]">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - login form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-accent-600 mb-1">Oximax agritech Pvt Ltd</p>
            <h1 className="text-4xl font-black tracking-tight leading-none bg-gradient-to-r from-primary-700 via-primary-500 to-accent-500 bg-clip-text text-transparent">DMS</h1>
            <p className="mt-1 text-xs font-medium tracking-[0.16em] uppercase text-primary-700 dark:text-primary-300">Dairy Management System</p>
          </div>

          {/* Form */}
          <div className="bg-white dark:bg-surface-800 rounded-2xl shadow-card border border-surface-200 dark:border-surface-700 p-6 sm:p-8">
            {step === 'shifts' ? (
              <div>
                <button
                  type="button"
                  className="text-sm text-primary-700 hover:underline mb-4"
                  onClick={() => { setStep('staff'); setError(''); }}
                >
                  Back to sign in
                </button>
                <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">QC Login</h2>
                <p className="text-sm text-surface-400 mb-6">Choose the shift this account is authorized for</p>
                <div className="grid grid-cols-1 gap-3">
                  {QC_SHIFTS.map((item) => (
                    <button
                      key={item.shift}
                      type="button"
                      onClick={() => openQcShift(item.shift)}
                      className="w-full rounded-xl border border-surface-200 dark:border-surface-600 px-4 py-3 text-left hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                    >
                      <span className="block text-sm font-semibold text-surface-900 dark:text-surface-100">{item.label}</span>
                      <span className="block text-xs text-surface-400 mt-0.5">Raw material, SMP, and Butter for this shift only</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
            <>
            <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-1">
              {step === 'qc' ? `Shift ${qcShift} login` : 'Sign in'}
            </h2>
            <p className="text-sm text-surface-400 mb-6">
              {step === 'qc'
                ? 'Enter the user ID and password authorized for this shift'
                : 'Enter your credentials to access the dashboard'}
            </p>
            {step === 'qc' && (
              <button
                type="button"
                className="text-sm text-primary-700 hover:underline mb-4"
                onClick={() => { setStep('shifts'); setError(''); setUsername(''); setPassword(''); }}
              >
                Change shift
              </button>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                  {step === 'qc' ? 'User ID' : 'Username'}
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
                  className="input"
                  placeholder={step === 'qc' ? 'Enter your user ID' : 'Enter your username'}
                  required
                  autoComplete="username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                    className="input pr-10"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlashIcon className="h-4.5 w-4.5" /> : <EyeIcon className="h-4.5 w-4.5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
                >
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500/30" />
                  <span className="text-sm text-surface-500">Remember me</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-2.5 text-sm font-semibold"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-surface-100 dark:border-surface-700">
              <p className="text-xs font-medium text-surface-400 uppercase tracking-wider mb-3">
                {step === 'qc' ? `Shift ${qcShift} demo` : 'Quick demo access'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {step === 'qc'
                  ? QC_SHIFTS.filter((item) => item.shift === qcShift).map((role) => (
                      <button
                        key={role.shift}
                        type="button"
                        onClick={() => fillDemo(role)}
                        className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors
                          bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300
                          hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-400"
                      >
                        {role.username}
                      </button>
                    ))
                  : (
                    <>
                      {DEMO_ROLES.map(role => (
                        <button
                          key={role.label}
                          type="button"
                          onClick={() => fillDemo(role)}
                          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors
                            bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300
                            hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-400"
                        >
                          {role.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => { setStep('shifts'); setError(''); setUsername(''); setPassword(''); }}
                        className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors
                          bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-300
                          hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-900/20 dark:hover:text-primary-400"
                      >
                        QC Login
                      </button>
                    </>
                  )}
              </div>
            </div>
            </>
            )}
          </div>

          <p className="text-center text-xs text-surface-400 mt-6">
            Made in India · GST Compliant · MCA Audit Ready
          </p>
        </div>
      </div>
    </div>
  );
}

